<template>
  <PunchoutSessionGuard />

  <component :is="layout">
    <RouterView />
  </component>

  <ModalHost />
  <NotificationsHost />
  <EnvironmentBadge />
  <CartBottomBar />

  <PriceRequestDialog />
  <VolumePricingDialog />
  <AvailabilityNoteDialog />
  <SupportDocumentsDialog />
</template>

<script setup lang="ts">
import { useHead } from "@unhead/vue";
import { computedEager } from "@vueuse/core";
import { markRaw, onMounted, onBeforeUnmount } from "vue";
import { useRoute, useRouter } from "vue-router";
import { setupBroadcastGlobalListeners } from "@/broadcast";
import { globals } from "@/core/globals";
import { useFileDownloadTracker } from "@/shared/gtm/download-counter";
import { ModalHost } from "@/shared/modal";
import { NotificationsHost } from "@/shared/notification";
import { eventBus } from "@/shared/thorlabs/shared";
import { useEditMode } from "@/shared/thorlabs/shared/composables";
import { useHreflangCanonical, useWhiteLabeling, usePageHead } from "./core/composables";
import { MainLayout, SecureLayout, EmptyLayout, useSearchBar } from "./shared/layout";
import type { Component } from "vue";
import CartBottomBar from "@/shared/cart/components/cart-bottom-bar.vue";
import EnvironmentBadge from "@/shared/layout/components/environmentBadge/environment-badge.vue";
import AvailabilityNoteDialog from "@/shared/thorlabs/shared/components/availability-note-dialog.vue";
import PriceRequestDialog from "@/shared/thorlabs/shared/components/price-request-dialog.vue";
import PunchoutSessionGuard from "@/shared/thorlabs/shared/components/punchout-session-guard.vue";
import SupportDocumentsDialog from "@/shared/thorlabs/shared/components/support-documents-dialog.vue";
import VolumePricingDialog from "@/shared/thorlabs/shared/components/volume-pricing-dialog.vue";

/** NOTE: As an example, here is the code for getting the settings from Liquid work context. */
const _props = withDefaults(defineProps<{ settings?: string }>(), { settings: "{}" });

const _settings = JSON.parse(_props.settings); // eslint-disable-line @typescript-eslint/no-unused-vars

const route = useRoute();
const router = useRouter();
const { hideSearchBar, hideSearchDropdown } = useSearchBar();
const { favIcons } = useWhiteLabeling();
const { isEditable, isPreview } = useEditMode();

useHead({
  link: favIcons.value,
});

usePageHead({
  meta: {
    "og:image": `${globals.optimizelyUrl}/globalassets/images/meta/og-logo.png`,
  },
});

useHreflangCanonical();

const layouts: Record<NonNullable<typeof route.meta.layout>, Component> = {
  Main: markRaw(MainLayout),
  Secure: markRaw(SecureLayout),
  Empty: markRaw(EmptyLayout),
};

const layout = computedEager(() => layouts[route.meta?.layout ?? "Main"]);

router.beforeEach((to) => {
  // Hiding the drop-down list of search results
  hideSearchDropdown();

  // Hiding the search bar on mobile devices
  if (to.name !== "Search") {
    hideSearchBar();
  }
});

const { gtmTrackFileDownload, isDownloadableFile } = useFileDownloadTracker();

const handleAnchorClick = (event: MouseEvent) => {
  const target = event.target;
  if (!(target instanceof Element)) {
    return;
  }
  const link: Element | null = target.closest("a");
  const href = link instanceof HTMLAnchorElement ? link.href : link?.getAttribute("href");
  if (!link || !href) {
    return;
  }

  const download = link.getAttribute("download") ?? "";
  const isExtensionInUrl = isDownloadableFile(href, false);
  const isExtensionInDownload = download ? isDownloadableFile(download, true) : false;
  const url = isExtensionInUrl ? href : isExtensionInDownload ? download : "";
  if (!isExtensionInUrl && !isExtensionInDownload) {
    return;
  }

  gtmTrackFileDownload(url, isExtensionInDownload);
};

const handleLinkClick = async (event: MouseEvent | TouchEvent) => {
  if (isPreview.value) {
    return;
  }

  if ((event as MouseEvent).ctrlKey) {
    return;
  }

  const target = event.target;
  if (!(target instanceof Element)) {
    return;
  }
  const anchor = target.closest("a");

  let href = anchor?.getAttribute("href");
  if (anchor && href && href.startsWith("/") && !href.includes("#") && anchor.getAttribute("target") !== "_blank") {
    try {
      event.preventDefault();
      const regex = /^\/(en|zh|ja|de|fr)\//i;
      href = regex.test(href) ? href.slice(3) : href;
      await router.push(href);
      eventBus.emit("closeMenu");
    } catch (e) {
      /* empty */
    }
  }
};

onMounted(setupBroadcastGlobalListeners);
onMounted(() => {
  document.addEventListener("click", handleAnchorClick);
  if (!isEditable.value) {
    document.addEventListener("click", handleLinkClick);
  }
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleAnchorClick);
  document.removeEventListener("click", handleLinkClick);
  eventBus.off("closeMenu");
});
</script>

<style lang="scss">
@import "assets/styles/main.scss";
</style>
